import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  alpha,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload,
  Refresh,
  Save,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Description,
  DataObject,
  Inventory,
  Store,
  SelectAll,
  ClearAll,
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import { ProgressBar, PageLoader } from '../components/common/LoadingState';
import SearchInput from '../components/common/SearchInput';
import api from '../services/api';

const steps = [
  { label: 'Ingresar SKUs', icon: <Description /> },
  { label: 'Descargar Datos', icon: <CloudUpload /> },
  { label: 'Seleccionar Campos', icon: <DataObject /> },
  { label: 'Completado', icon: <CheckCircle /> },
];

const SpecsImport = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [skusInput, setSkusInput] = useState('');
  const [lang, setLang] = useState('PT');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Selection mode: 'manual' (text input) or 'select' (from products list)
  const [selectionMode, setSelectionMode] = useState('select');
  
  // Retail and products for quick selection
  const [retails, setRetails] = useState([]);
  const [selectedRetailId, setSelectedRetailId] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProductSkus, setSelectedProductSkus] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  // Field selection
  const [availableFields, setAvailableFields] = useState([]);
  const [fieldExamples, setFieldExamples] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  
  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [retailsRes, productsRes] = await Promise.all([
          api.get('/retails'),
          api.get('/products?limit=10000'), // Get all products for selection
        ]);
        setRetails(retailsRes.data);
        // Handle paginated response structure { data: [...], total, page, limit }
        const productsData = productsRes.data?.data || productsRes.data || [];
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (err) {
        setError('Error al cargar datos iniciales');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchData();
  }, []);

  // Poll job status
  useEffect(() => {
    let interval;
    if (jobId && jobStatus?.status === 'processing') {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/specs/import/status/${jobId}`);
          setJobStatus(response.data);

          if (response.data.status === 'completed') {
            const fields = response.data.allFields || [];
            const examples = response.data.fieldExamples || {};
            const errors = response.data.errors || [];
            
            setAvailableFields(fields);
            setFieldExamples(examples);
            
            if (fields.length === 0) {
              // No fields found - likely API error or no matching products
              if (errors.length > 0) {
                setError(`Error al obtener datos: ${errors.map(e => e.error).join(', ')}. Verifica las credenciales de la API en Configuración.`);
              } else {
                setError('No se encontraron especificaciones para los SKUs ingresados. Verifica que los SKUs existan en la API externa.');
              }
              setActiveStep(0);
            } else {
              setActiveStep(2);
            }
          }
        } catch (err) {
          console.error('Error polling job status:', err);
          if (err.response?.status === 404) {
            setError('La sesión de importación expiró. Por favor, inicia una nueva importación.');
            setActiveStep(0);
          }
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [jobId, jobStatus?.status]);

  const parseSkus = () => {
    return skusInput
      .split(/[\n,;]+/)
      .map(sku => sku.trim())
      .filter(sku => sku.length > 0);
  };
  
  // Get SKUs based on selection mode
  const getSkusToImport = () => {
    if (selectionMode === 'manual') {
      return parseSkus();
    } else {
      return selectedProductSkus;
    }
  };
  
  // Get filtered products for the selected retail
  const filteredProducts = products.filter(p => {
    if (!selectedRetailId) return false;
    if (p.retailId !== selectedRetailId) return false;
    
    // Apply search filter
    if (productSearch) {
      const search = productSearch.toLowerCase();
      const title = p.feedData?.title || p.title || '';
      const sku = p.sku || '';
      const brand = p.feedData?.brand || p.brand || '';
      if (!title.toLowerCase().includes(search) && 
          !sku.toLowerCase().includes(search) &&
          !brand.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Apply brand filter
    if (brandFilter) {
      const brand = (p.feedData?.brand || p.brand || '').toLowerCase();
      if (brand !== brandFilter.toLowerCase()) {
        return false;
      }
    }
    
    return true;
  });
  
  // Get unique brands for the selected retail
  const availableBrands = [...new Set(
    products
      .filter(p => p.retailId === selectedRetailId)
      .map(p => p.feedData?.brand || p.brand)
      .filter(Boolean)
  )].sort();
  
  // Toggle product selection
  const toggleProductSelection = (sku) => {
    if (selectedProductSkus.includes(sku)) {
      setSelectedProductSkus(prev => prev.filter(s => s !== sku));
    } else {
      if (selectedProductSkus.length >= 100) {
        setError('Máximo 100 SKUs por importación');
        return;
      }
      setSelectedProductSkus(prev => [...prev, sku]);
    }
  };
  
  // Select all visible products
  const selectAllVisible = () => {
    const newSkus = filteredProducts.map(p => p.sku).filter(sku => !selectedProductSkus.includes(sku));
    const totalWillBe = selectedProductSkus.length + newSkus.length;
    if (totalWillBe > 100) {
      setError('Máximo 100 SKUs por importación. Selecciona menos productos.');
      return;
    }
    setSelectedProductSkus(prev => [...prev, ...newSkus]);
  };
  
  // Deselect all
  const deselectAll = () => {
    setSelectedProductSkus([]);
  };

  const handleStartImport = async () => {
    const skus = getSkusToImport();

    if (skus.length === 0) {
      setError('Selecciona al menos un SKU');
      return;
    }

    if (skus.length > 100) {
      setError('Máximo 100 SKUs por importación');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/specs/import/start', { skus, lang });
      setJobId(response.data.jobId);
      setJobStatus({ status: 'processing', progress: 0 });
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar importación');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      if (selectedFields.length >= 60) {
        setError('Máximo 60 campos permitidos');
        return;
      }
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleFinalize = async () => {
    if (selectedFields.length === 0) {
      setError('Selecciona al menos un campo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/specs/import/finalize/${jobId}`, {
        selectedFields,
        lang,
      });

      if (response.data.savedCount > 0) {
        setSuccess(`Importación completada: ${response.data.savedCount} productos guardados`);
        setActiveStep(3);
      } else if (response.data.errors && response.data.errors.length > 0) {
        setError(`Importación completada con errores: ${response.data.errors.map(e => e.error).join(', ')}`);
        setActiveStep(3);
      } else {
        setError('No se guardaron productos. Verifica que los SKUs existan en el sistema.');
      }
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = errorData?.error || 'Error al finalizar importación';
      
      // Add more context based on error code
      if (errorData?.code === 'JOB_NOT_FOUND') {
        errorMessage = 'La sesión de importación expiró. Por favor, inicia una nueva importación.';
        // Reset to step 0
        setActiveStep(0);
      } else if (errorData?.code === 'NO_RESULTS') {
        errorMessage = 'No se encontraron datos para importar. Verifica:\n- Las credenciales de la API en Configuración\n- Que los SKUs ingresados existan en la API externa';
        if (errorData?.errors?.length > 0) {
          errorMessage += `\n\nErrores: ${errorData.errors.map(e => e.error || e.message).join(', ')}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSkusInput('');
    setSelectedProductSkus([]);
    setSelectedRetailId('');
    setProductSearch('');
    setBrandFilter('');
    setJobId(null);
    setJobStatus(null);
    setAvailableFields([]);
    setFieldExamples({});
    setSelectedFields([]);
    setError('');
    setSuccess('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        const skusCount = getSkusToImport().length;
        
        return (
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Selecciona los SKUs de los productos para importar sus especificaciones desde la API externa.
            </Typography>

            {/* Language and Mode Selection */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Idioma</InputLabel>
                  <Select
                    value={lang}
                    label="Idioma"
                    onChange={(e) => setLang(e.target.value)}
                  >
                    <MenuItem value="ES">Español</MenuItem>
                    <MenuItem value="PT">Portugués</MenuItem>
                    <MenuItem value="EN">Inglés</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Tabs for selection mode */}
            <Tabs
              value={selectionMode}
              onChange={(e, v) => setSelectionMode(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                value="select" 
                label="Seleccionar de Productos" 
                icon={<Store sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
              <Tab 
                value="manual" 
                label="Ingresar SKUs Manualmente" 
                icon={<Description sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
            </Tabs>

            {selectionMode === 'select' ? (
              // Selection mode: Select from products list
              <Box>
                <Grid container spacing={3}>
                  {/* Retail Selection */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Retail *</InputLabel>
                      <Select
                        value={selectedRetailId}
                        label="Retail *"
                        onChange={(e) => {
                          setSelectedRetailId(e.target.value);
                          setSelectedProductSkus([]);
                          setBrandFilter('');
                          setProductSearch('');
                        }}
                      >
                        {retails.map((retail) => (
                          <MenuItem key={retail.id} value={retail.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Store sx={{ fontSize: 18, color: 'text.secondary' }} />
                              {retail.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Brand Filter */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!selectedRetailId}>
                      <InputLabel>Filtrar por Marca</InputLabel>
                      <Select
                        value={brandFilter}
                        label="Filtrar por Marca"
                        onChange={(e) => setBrandFilter(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Todas las marcas</em>
                        </MenuItem>
                        {availableBrands.map((brand) => (
                          <MenuItem key={brand} value={brand}>
                            {brand}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Search */}
                  <Grid item xs={12}>
                    <SearchInput
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Buscar por SKU, título o marca..."
                      fullWidth
                      disabled={!selectedRetailId}
                    />
                  </Grid>

                  {/* Selection Actions */}
                  {selectedRetailId && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<SelectAll />}
                            onClick={selectAllVisible}
                            disabled={filteredProducts.length === 0}
                          >
                            Seleccionar Todos ({filteredProducts.length})
                          </Button>
                          <Button
                            size="small"
                            startIcon={<ClearAll />}
                            onClick={deselectAll}
                            disabled={selectedProductSkus.length === 0}
                            color="inherit"
                          >
                            Deseleccionar
                          </Button>
                        </Box>
                        <Chip
                          label={`${selectedProductSkus.length} seleccionados`}
                          color={selectedProductSkus.length > 0 ? 'primary' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* Products List */}
                  <Grid item xs={12}>
                    {!selectedRetailId ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          borderStyle: 'dashed',
                          borderRadius: 2,
                        }}
                      >
                        <Store sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          Selecciona un retail para ver sus productos
                        </Typography>
                      </Paper>
                    ) : filteredProducts.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          borderRadius: 2,
                        }}
                      >
                        <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No se encontraron productos
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          maxHeight: 400,
                          overflow: 'auto',
                          borderRadius: 2,
                        }}
                      >
                        <List dense disablePadding>
                          {filteredProducts.map((product) => {
                            const isSelected = selectedProductSkus.includes(product.sku);
                            return (
                              <ListItem
                                key={product.id}
                                button
                                onClick={() => toggleProductSelection(product.sku)}
                                sx={{
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  backgroundColor: isSelected
                                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                                    : 'transparent',
                                  '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    edge="start"
                                    tabIndex={-1}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={product.feedData?.title || product.title || product.sku}
                                  secondary={
                                    <Box component="span">
                                      <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
                                        SKU: {product.sku}
                                      </Typography>
                                      {(product.feedData?.brand || product.brand) && (
                                        <Typography component="span" variant="caption" color="text.secondary">
                                          {' • '}{product.feedData?.brand || product.brand}
                                        </Typography>
                                      )}
                                      {product.feedData?.mpn && (
                                        <Typography component="span" variant="caption" color="text.secondary">
                                          {' • MPN: '}{product.feedData.mpn}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                  }}
                                />
                                {product.specs && Object.keys(product.specs).length > 0 && (
                                  <Chip
                                    label="Con specs"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Paper>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ) : (
              // Manual mode: Text input for SKUs
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    label="SKUs de productos"
                    placeholder="4A8D5A, F9J81A, C9380A..."
                    value={skusInput}
                    onChange={(e) => setSkusInput(e.target.value)}
                    helperText={`SKUs detectados: ${parseSkus().length}`}
                  />
                </Grid>
              </Grid>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleStartImport}
                disabled={loading || skusCount === 0}
                startIcon={<CloudUpload />}
                size="large"
              >
                {loading ? 'Iniciando...' : `Importar Specs (${skusCount} SKUs)`}
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Descargando especificaciones
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Por favor espera mientras se descargan los datos...
            </Typography>

            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
              <ProgressBar
                value={jobStatus?.progress || 0}
                label={`${jobStatus?.processedSkus || 0} / ${jobStatus?.totalSkus || 0} productos procesados`}
              />
            </Box>

            {jobStatus?.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 4, maxWidth: 400, mx: 'auto' }}>
                {jobStatus.errors.length} errores durante la descarga
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Selecciona los campos que deseas importar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleccionados: <strong>{selectedFields.length}</strong> / 60 campos
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {availableFields.map((field) => {
                const isSelected = selectedFields.includes(field);
                const examples = fieldExamples[field] || [];

                return (
                  <Grid item xs={12} sm={6} md={4} key={field}>
                    <Paper
                      variant="outlined"
                      onClick={() => toggleField(field)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: 2,
                        borderWidth: isSelected ? 2 : 1,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected
                          ? (theme) => alpha(theme.palette.primary.main, 0.04)
                          : 'background.paper',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{ mt: -0.5, ml: -1, mr: 0.5 }}
                        />
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {field}
                          </Typography>
                          {examples.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Ej: {examples.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={() => setActiveStep(0)}
                startIcon={<ArrowBack />}
              >
                Volver
              </Button>
              <Button
                variant="contained"
                onClick={handleFinalize}
                disabled={loading || selectedFields.length === 0}
                startIcon={<Save />}
              >
                {loading ? 'Guardando...' : 'Guardar Especificaciones'}
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>
              Importación Completada
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Las especificaciones han sido guardadas exitosamente.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<Refresh />}
              >
                Nueva Importación
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/products')}
                startIcon={<Inventory />}
              >
                Ver Productos
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (initialLoading) {
    return <PageLoader message="Cargando datos..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Importar Especificaciones"
        subtitle="Importa especificaciones técnicas desde la API externa"
      />

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: activeStep >= index
                          ? 'primary.main'
                          : (theme) => alpha(theme.palette.grey[500], 0.2),
                        color: activeStep >= index ? 'white' : 'text.secondary',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {activeStep > index ? (
                        <CheckCircle sx={{ fontSize: 18 }} />
                      ) : (
                        React.cloneElement(step.icon, { sx: { fontSize: 16 } })
                      )}
                    </Box>
                  )}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: activeStep === index ? 600 : 400,
                      color: activeStep >= index ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </CardContent>
      </Card>

      <Card
        sx={{
          backgroundColor: (theme) => alpha(theme.palette.info.main, 0.04),
          borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Información
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Los datos se descargan en lotes de 10 SKUs para optimizar el rendimiento.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Las especificaciones se guardan asociadas al SKU e idioma seleccionado.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Si un producto ya existe, sus especificaciones serán actualizadas.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SpecsImport;
